/**
 * 路由权限守卫 - 基础设施层
 * 集成到Vue Router的权限守卫
 */

import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { ElMessage } from 'element-plus'
import { VuePermissionAdapter } from './VuePermissionAdapter'

/**
 * 路由权限守卫配置
 */
export interface PermissionGuardConfig {
  adapter: VuePermissionAdapter
  loginRoute?: string
  forbiddenRoute?: string
  showWarningMessages?: boolean
  whitelist?: string[] // 白名单路由（不需要权限检查）
}

/**
 * 创建路由权限守卫
 */
export function createPermissionGuard(config: PermissionGuardConfig) {
  const {
    adapter,
    loginRoute = '/login',
    forbiddenRoute = '/403',
    showWarningMessages = true,
    whitelist = ['/login', '/403', '/404', '/500']
  } = config

  /**
   * Vue Router权限守卫函数
   */
  return async function permissionGuard(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ): Promise<void> {
    // 检查是否为白名单路由
    if (whitelist.includes(to.path)) {
      next()
      return
    }

    // 检查用户是否已登录
    const currentUser = adapter.getCurrentUser().value
    if (!currentUser) {
      // 未登录，重定向到登录页
      if (showWarningMessages) {
        ElMessage.warning('请先登录')
      }
      next(loginRoute)
      return
    }

    // 检查路由权限
    const routePermissions = to.meta?.permissions as string[] | undefined
    if (!routePermissions || routePermissions.length === 0) {
      // 路由无权限要求，允许访问
      next()
      return
    }

    // 检查用户是否有权限访问
    const hasPermission = adapter.checkRoutePermission(routePermissions, to.path)
    
    if (hasPermission) {
      next()
    } else {
      // 无权限，重定向到403页面
      if (showWarningMessages) {
        ElMessage.error('无权限访问此页面')
      }
      next(forbiddenRoute)
    }
  }
}

/**
 * 创建路由权限检查函数（不中断导航）
 */
export function createRoutePermissionChecker(adapter: VuePermissionAdapter) {
  return function checkRoutePermission(route: RouteLocationNormalized): {
    hasPermission: boolean
    missingPermissions: string[]
    userRole: string
    userIndustryCode: string
  } {
    const currentUser = adapter.getCurrentUser().value
    const routePermissions = route.meta?.permissions as string[] | undefined

    if (!currentUser) {
      return {
        hasPermission: false,
        missingPermissions: routePermissions || [],
        userRole: '',
        userIndustryCode: ''
      }
    }

    if (!routePermissions || routePermissions.length === 0) {
      return {
        hasPermission: true,
        missingPermissions: [],
        userRole: currentUser.role,
        userIndustryCode: currentUser.industryCode
      }
    }

    // 检查缺少的权限
    const missingPermissions: string[] = []
    
    routePermissions.forEach(permission => {
      if (!adapter.hasPermission(permission)) {
        missingPermissions.push(permission)
      }
    })

    return {
      hasPermission: missingPermissions.length === 0,
      missingPermissions,
      userRole: currentUser.role,
      userIndustryCode: currentUser.industryCode
    }
  }
}

/**
 * 创建菜单权限过滤器
 */
export function createMenuPermissionFilter(adapter: VuePermissionAdapter) {
  return function filterMenuByPermission(menuItems: any[]): any[] {
    return menuItems.filter(menuItem => {
      // 如果菜单项有子菜单，递归过滤
      if (menuItem.children && menuItem.children.length > 0) {
        const filteredChildren = filterMenuByPermission(menuItem.children)
        if (filteredChildren.length > 0) {
          menuItem.children = filteredChildren
          return true
        }
        return false
      }

      // 检查菜单项权限
      const menuPermissions = menuItem.meta?.permissions as string[] | undefined
      if (!menuPermissions || menuPermissions.length === 0) {
        return true // 无权限要求，默认显示
      }

      return adapter.hasAnyPermission(menuPermissions)
    })
  }
}

/**
 * 创建按钮权限检查器
 */
export function createButtonPermissionChecker(adapter: VuePermissionAdapter) {
  return function checkButtonPermission(permissions: string | string[]): {
    hasPermission: boolean
    check: () => boolean
    disabled: boolean
  } {
    const permissionArray = Array.isArray(permissions) ? permissions : [permissions]
    const hasPermission = adapter.hasAnyPermission(permissionArray)
    
    const check = (): boolean => {
      if (!hasPermission) {
        ElMessage.warning('无权限执行此操作')
        return false
      }
      return true
    }

    return {
      hasPermission,
      check,
      disabled: !hasPermission
    }
  }
}

/**
 * 创建权限指令工厂
 */
export function createPermissionDirectiveFactory(adapter: VuePermissionAdapter) {
  return {
    /**
     * 权限指令 v-permission
     */
    permission: {
      mounted(el: HTMLElement, binding: any) {
        const permissions = Array.isArray(binding.value) ? binding.value : [binding.value]
        const hasPermission = adapter.hasAnyPermission(permissions)
        
        if (!hasPermission) {
          el.style.display = 'none'
        }
      },
      updated(el: HTMLElement, binding: any) {
        const permissions = Array.isArray(binding.value) ? binding.value : [binding.value]
        const hasPermission = adapter.hasAnyPermission(permissions)
        
        if (hasPermission) {
          el.style.display = ''
        } else {
          el.style.display = 'none'
        }
      }
    },

    /**
     * 权限指令 v-permission-if
     */
    permissionIf: {
      mounted(el: HTMLElement, binding: any) {
        const permissions = Array.isArray(binding.value) ? binding.value : [binding.value]
        const hasPermission = adapter.hasAnyPermission(permissions)
        
        if (!hasPermission) {
          el.parentNode?.removeChild(el)
        }
      }
    },

    /**
     * 权限指令 v-permission-disabled
     */
    permissionDisabled: {
      mounted(el: HTMLElement, binding: any) {
        const permissions = Array.isArray(binding.value) ? binding.value : [binding.value]
        const hasPermission = adapter.hasAnyPermission(permissions)
        
        if (!hasPermission) {
          el.setAttribute('disabled', 'disabled')
          el.classList.add('is-disabled')
        }
      },
      updated(el: HTMLElement, binding: any) {
        const permissions = Array.isArray(binding.value) ? binding.value : [binding.value]
        const hasPermission = adapter.hasAnyPermission(permissions)
        
        if (hasPermission) {
          el.removeAttribute('disabled')
          el.classList.remove('is-disabled')
        } else {
          el.setAttribute('disabled', 'disabled')
          el.classList.add('is-disabled')
        }
      }
    }
  }
}